import * as React from 'react'
import { ServerDataTable, type TableRow, type TableState } from '@/components/visitone/server-data-table'
import { RefusedSurface } from '@/components/visitone/surfaces/access-surface'
import { PeopleFrame } from '@/components/visitone/surfaces/people/people-shared'
import { useT } from '@/components/visitone/visitone-provider'
import { useAccess } from '@/hooks/use-access'
import { useAccessChanges } from '@/hooks/use-people'
import { PeopleRequestError, type AccessChange } from '@/lib/bff/people'
import { formatTenantDateTime } from '@/lib/format/tenant-date'
import { describeChange } from '@/lib/people/people-text'

const ROLE_KINDS = new Set<AccessChange['kind']>(['roleCreated', 'roleEdited', 'roleDeleted'])

/*
 * Setup › People & access › Access changes (VIS-332): who changed whose
 * access, newest first, in the tenant's time zone. The route renders this
 * only with `access_changes.view`. Names are as they were at the time.
 */
export function AccessChangesSurface() {
  const t = useT()
  const { tenantName: tenant } = useAccess()
  const [page, setPage] = React.useState(0)
  const changes = useAccessChanges(page)
  const entries = changes.page?.data ?? []
  const timeZone = changes.page?.timeZone

  if (changes.error instanceof PeopleRequestError && changes.error.status === 403) return <RefusedSurface />

  let state: TableState
  if (changes.error) state = 'error'
  else if (!changes.page) state = 'loading'
  else if (entries.length === 0) state = 'empty'
  else state = 'data'

  const rows: TableRow[] = entries.map((entry) => {
    const when = formatTenantDateTime(entry.at, timeZone)
    const whose = ROLE_KINDS.has(entry.kind)
      ? { primary: t('people.changes.role', { role: entry.subject.roleName ?? t('people.changes.noName') }) }
      : {
          primary: entry.subject.name ?? entry.subject.email ?? t('people.changes.noName'),
          secondary: entry.subject.name && entry.subject.email ? entry.subject.email : undefined,
        }
    return {
      id: entry.id,
      label: `${when} ${whose.primary}`,
      cells: {
        when: { primary: when },
        by: {
          primary: entry.actorName ?? t('people.changes.noName'),
          pills: entry.actorKind === 'landlord' ? [{ tone: 'info' as const, label: t('people.changes.visitOne') }] : undefined,
        },
        whose,
        what: { primary: describeChange(t, entry) },
      },
    }
  })

  const pagination = changes.page?.pagination

  return (
    <PeopleFrame pageTitle={t('people.changes.title')} summary={t('people.changes.summary', { tenant })}>
      <ServerDataTable
        layout="stacked"
        state={state}
        onRetry={() => void changes.revalidate()}
        columns={[
          { key: 'when', label: t('people.changes.whenColumn'), width: '11rem' },
          { key: 'by', label: t('people.changes.byColumn') },
          { key: 'whose', label: t('people.changes.whoseColumn') },
          { key: 'what', label: t('people.changes.whatColumn') },
        ]}
        rows={rows}
        sortedBy={{ key: 'when', direction: 'desc' }}
        caption={t('people.changes.caption', { tenant })}
        regionLabel={t('people.changes.caption', { tenant })}
        footer={
          state === 'loading'
            ? t('people.changes.loading')
            : t('people.changes.count', { count: pagination?.totalCount ?? entries.length })
        }
        pagination={pagination ? { page: pagination.page, totalPages: pagination.totalPages, onPageChange: setPage } : undefined}
        loadingRowCount={5}
        messages={{
          loadErrorTitle: t('people.changes.loadFailed.title'),
          loadErrorBody: t('people.changes.loadFailed.body'),
          retry: t('people.users.loadFailed.retry'),
        }}
        empty={{ title: t('people.changes.empty.title'), body: t('people.changes.empty.body', { tenant }) }}
      />
    </PeopleFrame>
  )
}
